import { cva, type VariantProps } from "class-variance-authority";
import type { ReactNode, Ref } from "react";
import type { ViewInstance } from "react-native";
import { ActivityIndicator, Pressable, type PressableProps, Text } from "react-native";

import { cn } from "@/lib/ui/cn";
import { expressiveBrand } from "@/theme/expressive";
import { useThemeColors } from "@/theme/theme";

const button = cva("flex-row items-center justify-center rounded-lg", {
  variants: {
    variant: {
      default: "bg-primary active:opacity-90",
      // The cobalt `primary` itself, which is never swapped for lime: an add
      // that sits beside the lime Sell (the scan queue) must not read as a second Sell.
      primary: "bg-primary active:opacity-90",
      outline: "border border-border bg-transparent active:bg-surface-muted",
      ghost: "bg-transparent active:bg-surface-muted",
      destructive: "bg-loss active:opacity-90",
    },
    size: { sm: "h-9 px-3", default: "h-12 px-4", lg: "h-12 px-6", icon: "h-12 w-12" },
  },
  defaultVariants: { variant: "default", size: "default" },
});

const label = cva("text-[15px] font-title", {
  variants: {
    variant: {
      default: "text-primary-foreground",
      primary: "text-primary-foreground",
      outline: "text-foreground",
      ghost: "text-foreground",
      destructive: "text-primary-foreground",
    },
  },
  defaultVariants: { variant: "default" },
});

type Props =
  & PressableProps
  & VariantProps<typeof button>
  & {
    title: string;
    className?: string;
    children?: ReactNode;
    /** Swaps the label for a spinner and blocks presses while work is in flight. */
    loading?: boolean;
    /** Declared so focus can be handed to a button; React passes it through `rest` already. */
    ref?: Ref<ViewInstance>;
  };

/**
 * The shared button.
 *
 * Besides default and pressed it carries two required states: `disabled` and `loading` dim to 50%
 * and block presses, so a frozen Save never looks tappable, and `loading` swaps the label for a
 * spinner tinted to match the variant's text.
 */
export function Button(
  { title, variant, size, className, children, loading, disabled, ...rest }: Props,
) {
  const c = useThemeColors();
  const isDisabled = disabled || loading;
  const spinnerColor = variant === "outline" || variant === "ghost"
    ? c.foreground
    : variant === "destructive" || variant === "primary"
    ? c.primaryForeground
    : expressiveBrand.navy;
  return (
    <Pressable
      className={cn(button({ variant, size }), isDisabled && "opacity-50", className)}
      style={[
        { borderRadius: 28 },
        (!variant || variant === "default") && { backgroundColor: expressiveBrand.lime },
      ]}
      disabled={isDisabled}
      // The role is set on this primitive rather than at each call site, because on web a Pressable
      // renders a bare `div[tabindex]` that a screen reader announces as text. The name is supplied
      // only when no title text is rendered: an icon button (a `children` icon replaces the label)
      // or a `loading` button, whose title is replaced by a spinner. A normal button already
      // announces its title, and a second label would make the name ambiguous. Callers can override
      // either through `rest`.
      accessibilityRole="button"
      accessibilityLabel={children || loading ? title : undefined}
      accessibilityState={{ disabled: !!isDisabled, busy: !!loading }}
      // sm stays visually compact while meeting Android's 48dp touch-target floor.
      hitSlop={size === "sm" ? 6 : undefined}
      {...rest}
    >
      {loading
        // Hidden from assistive tech: the button already announces its title and busy state, and
        // the spinner would otherwise be a second, unnamed progressbar inside it.
        ? <ActivityIndicator aria-hidden color={spinnerColor} />
        // An icon child (for example the FAB) takes precedence over the text label.
        : children ?? (
          <Text
            className={label({ variant })}
            style={!variant || variant === "default" ? { color: expressiveBrand.navy } : undefined}
          >
            {title}
          </Text>
        )}
    </Pressable>
  );
}
